<template>
  <Layout>
    <!-- Header -->
    <div class="row">
      <div class="col-10">
        <PageHeader title="Accident Tracking and Management" />
      </div>
      <div class="col-2">
        <button type="button" :class="isDataFilterActive
          ? 'btn btn-success toggle-accidents-filter w-100'
          : 'btn btn-primary toggle-accidents-filter w-100'
          " @click="toggleFilter">
          <span v-if="!isDataFilterActive"><i class="bx bx-cog bx-spin toggle-accidents-filter"></i> Select
            Filters
          </span>
          <span v-else><i class="bx bx-check toggle-accidents-filter mr-4"></i>Filters
            Applied</span>
        </button>
      </div>
    </div>
    <hr />
    <!-- Statistics -->
    <div class="row">
      <div class="col-sm-4">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="me-3">
                <p class="text-muted mb-2">
                  {{
                    $t("menuitems.incident.list.accident.list.totalAccidents")
                  }}
                </p>
                <h5 class="mb-0">
                  {{ accidentStatistics.totalAccidents }}
                </h5>
              </div>

              <div class="avatar-sm ms-auto">
                <div class="avatar-title bg-light rounded-circle text-danger font-size-20">
                  <i class="bx bxs-car"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-4">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="me-3">
                <p class="text-muted mb-2">
                  {{
                    $t("menuitems.incident.list.accident.list.openAccidents")
                  }}
                </p>
                <h5 class="mb-0">
                  {{ accidentStatistics.openAccidents }}
                </h5>
              </div>

              <div class="avatar-sm ms-auto">
                <div class="avatar-title bg-light rounded-circle text-warning font-size-20">
                  <i class="bx bxs-folder-open"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-4">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="me-3">
                <p class="text-muted mb-2">
                  {{
                    $t(
                      "menuitems.incident.list.accident.list.ResolvedAccidents"
                    )
                  }}
                </p>
                <h5 class="mb-0">
                  {{ accidentStatistics.resolvedAccidents }}
                </h5>
              </div>

              <div class="avatar-sm ms-auto">
                <div class="avatar-title bg-light rounded-circle text-success font-size-20">
                  <i class="bx bxs-check-circle"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Incidents Table -->
    <Loader>
      <div class="table-responsive mb-0">
        <b-table class="datatables" :items="allIncidents" :fields="fields" responsive="sm" :per-page="0"
          :current-page="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :filter="filter"
          :filter-included-fields="filterOn" @filtered="onFiltered">
          <template #cell(source_id)="row">
            {{ !row.item.source_id ? "NA" : masters.incidentSourceMaster.find(r => r.id ===
              row.item.source_id).source_name }}
          </template>
          <template #cell(vehicle_no)="row">
            {{ !row.item.vehicle_no ? "NA" : row.item.vehicle_no }}
          </template>
          <template #cell(status)="row">
            <span :class="`badge ${getStatusClass(row.item.status)}`">
              {{ row.item.status }}
            </span>
          </template>
          <template #cell(actions)="row" class="text-end">
            <b-button size="sm" variant="primary" class="m-1" @click="viewAccidentDetailsPage(row.item)">
              View Details
            </b-button>
            <b-button v-if="row.item.status === 'Open' && row.item.reported_to" variant="info" class="m-1" size="sm"
              @click="showTrackIncidentDetails(row.item)">
              Track RPO
            </b-button>
            <button v-if="row.item.status !== 'Resolved'" class="btn btn-sm btn-success m-1"
              @click="openResolveAccidentPage(row.item)">
              Resolve
            </button>
          </template>
        </b-table>
      </div>
    </Loader>
    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-end mb-3">
          <ul class="pagination pagination-rounded mb-0">
            <!-- pagination -->
            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
          </ul>
        </div>
      </div>
    </div>

    <!-- Track Incident modal -->
    <b-modal id="track-incident-modal" title="Track Incident" title-class="font-18" size="xl" hide-footer centered
      no-close-on-esc no-close-on-backdrop>
      <template #modal-header>
        <div>
          <h5>RPO and Incident Live Tracker</h5>
        </div>
        <div>
          <router-link :to="`/incident/view-accident-details/${selectedIncident.id}`">
            <button class="btn btn-sm btn-outline-info m-2">
              Show Accident Details
            </button>
          </router-link>
          <button class="btn btn-sm btn-outline-danger" @click="closeTrackRPOModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
      </template>
      <div class="row">
        <div class="col-5">
          <p class="mb-2">
            Incident ID:
            <span class="text-primary">{{ selectedIncident.id }}</span>
          </p>
          <p class="mb-2">
            Vehicle Number:
            <span class="text-primary">{{
              selectedIncident.vehicle_no
                ? selectedIncident.vehicle_no
                : "Not available"
            }}</span>
          </p>
          <p class="mb-4">
            Date | Time:
            <span class="text-primary">{{
              new Date(selectedIncident.incident_datetime).toLocaleString()
            }}</span>
          </p>
          <hr />
          <h5>
            <i class="bx bxs-video-recording text-danger font-size-18 bx-fade-right"></i>
            <span class="m-3">Live</span>
          </h5>
          <div class="row" v-if="rpoDeviceStats">
            <div class="col-6">
              <button v-if="rpoGPS" :class="rpoGPS.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPO GPS Location
              </button>
            </div>
            <div class="col-6">
              <button v-if="rpoFrontCamera" :class="rpoFrontCamera.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPV Front Camera
              </button>
            </div>
            <div class="col-6">
              <button v-if="rpoRearCamera" :class="rpoRearCamera.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPV Rear Camera
              </button>
            </div>
            <div class="col-6">
              <button v-if="rpoBodyCamera" :class="rpoBodyCamera.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPO Body Camera
              </button>
            </div>
          </div>
          <div class="text-center" v-else>
            <p class="text-danger">RPO Device is Offline</p>
          </div>
          <hr />
          <ul class="verti-timeline list-unstyled">
            <li class="event-list active">
              <div class="event-timeline-dot">
                <i class="bx bxs-right-arrow-circle font-size-18 bx-fade-right"></i>
              </div>
              <div class="d-flex">
                <div class="me-3">
                  <h5 class="font-size-14">
                    Incident Closed
                    <i class="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
                  </h5>
                </div>
                <div class="flex-grow-1">
                  <div>14/03/2023 - 18:05:19</div>
                </div>
              </div>
            </li>
            <li class="event-list">
              <div class="event-timeline-dot">
                <i class="bx bx-right-arrow-circle font-size-18"></i>
              </div>
              <div class="d-flex">
                <div class="me-3">
                  <h5 class="font-size-14">
                    RPO Reported
                    <i class="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
                  </h5>
                </div>
                <div class="flex-grow-1">
                  <div>14/03/2023 - 17:56:10</div>
                </div>
              </div>
            </li>
            <li class="event-list">
              <div class="event-timeline-dot">
                <i class="bx bx-right-arrow-circle font-size-18"></i>
              </div>
              <div class="d-flex">
                <div class="me-3">
                  <h5 class="font-size-14">
                    Incident Created
                    <i class="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
                  </h5>
                </div>
                <div class="flex-grow-1">
                  <div>14/03/2023 - 17:40:37</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div class="col-7">
          <h5>RPO Activity Tracking</h5>
          <GoogleMap v-if="rpoDeviceStats" :location="rpoGPS" />
          <div class="text-center" v-else>
            <p class="text-danger mt-5">
              Unable to load location. RPO Device is Offline.
            </p>
          </div>
        </div>
      </div>
    </b-modal>

    <AccidentFilter :vehicleClassOptions="options.vehicleClass" @close="toggleFilter"
      @getFilteredIncidents="getFilteredIncidents" @resetFilter="resetFilter" />
  </Layout>
</template>

<script>
import Layout from "@/layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import DatePicker from "vue2-datepicker";
import VueEasyLightbox from "vue-easy-lightbox";
import Swal from "sweetalert2";
import incidentMixin from "../../mixins/incident";
import { mapActions } from "vuex";
import VueSlideBar from "vue-slide-bar";
import Loader from "../../components/widgets/loader.vue";
import "../../assets/plugins/range-slider/vue-range-slider.css";
import VueRangeSlider from "../../assets/plugins/range-slider/vue-range-slider";
import GoogleMap from "@/components/incidents/google-map";
import AccidentFilter from "@/components/incidents/accidents-filter";
/**
 * Dashboard Component
 */
export default {
  page: {
    title: "Incident Management",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },

  props: {
    updating: {
      type: Boolean,
    },
  },

  mixins: [incidentMixin],

  components: {
    Layout,
    PageHeader,
    VueEasyLightbox,
    DatePicker,
    VueSlideBar,
    Loader,
    VueRangeSlider,
    GoogleMap,
    AccidentFilter,
  },

  data() {
    return {
      isDataFilterActive: false,
      filterVehicleNumber: null,
      eventCategory: null,
      datepickervalueFrom: null,
      datepickervalueTo: null,
      vehicleNumber: null,
      vehicleClass: null,
      allIncidents: [],
      incidentId: null,
      visibleCaption: false,
      selectedIncident: {},
      index: 1,
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      sortBy: "id",
      sortDesc: true,
      filterOn: [],
      filter: null,
      loaderValue: false,
      operatorId: null,
      trackingInterval: null,
      rpoDeviceStats: null,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true,
        },
        {
          key: "ch_no",
          label: "Chainage No.",
          sortable: false,
        },
        {
          key: "source_id",
          label: "Source Name",
          sortable: false,
        },
        {
          key: "vehicle_class",
          label: "Vehicle Class",
          sortable: false,
        },
        {
          key: "reported_by_name",
          label: "Reported By",
          sortable: false,
        },
        {
          key: "direction",
          label: "Direction",
          sortable: false,
        },
        {
          key: "reported_to_name",
          label: "Assigned RPO",
          sortable: false,
        },
        {
          key: "reported_datetime",
          label: "Date | Time",
          thStyle: { width: "10%" },
          sortable: true,
          formatter: (value) => {
            return this.getReadableDateTime(value);
          },
        },
        {
          key: "closing-time",
          label: "Closing Time",
          sortable: false,
        },
        {
          key: "status",
          label: "Status",
          sortable: false,
          formatter: (value) => {
            return this.GetStatusMasterName(value);
          },
        },
        {
          key: "category",
          label: "Category",
          sortable: false,
        },
        {
          key: "actions",
          sortable: false,
          label: "Action",
        },
      ],

      options: {
        vehicleClass: [],
        informedTo: [],
        accidentLevel: [],
        eventCategory: [],
      },
      socketInterval: null,
    };
  },

  methods: {
    ...mapActions("incident", ["updateIncidents"]),

    toggleFilter() {
      document.body.classList.toggle("accidents-right-bar-enabled");
    },

    getStatusClass(status) {
      switch (status) {
        case "Open":
          return "bg-danger";

        case "In Progress":
          return "bg-warning";

        case "Resolved":
          return "bg-success";

        case "Closed":
          return "bg-success";

        default:
          return "bg-info";
      }
    },

    gotoRaiseNewIncidentPage() {
      this.$router.push("/incident/raise-incident");
    },

    viewAccidentDetailsPage(incident) {
      this.selectedIncident = incident;
      this.$router.push(
        `/incident/view-accident-details/${this.selectedIncident.id}`
      );
    },

    showTrackIncidentDetails(incident) {
      this.selectedIncident = incident;
      this.$bvModal.show("track-incident-modal");
      this.trackingInterval = setInterval(() => {
        this.$socket.emit(
          "REQUEST:RPO_LIVE_DETAILS",
          this.selectedIncident.reported_to
        );
      }, 3000);

      this.$socket.emit(
        "REQUEST:RPO_LIVE_DETAILS",
        this.selectedIncident.reported_to
      );
    },

    closeTrackRPOModal() {
      this.$bvModal.hide("track-incident-modal");
      if (this.trackingInterval) clearInterval(this.trackingInterval);
      this.trackingInterval = null;
      this.rpoDeviceStats = null;
    },

    openResolveAccidentPage(incident) {
      this.selectedIncident = incident;
      this.$router.push(
        `/incident/resolve-accident/${this.selectedIncident.id}`
      );
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    getReadableDateTime(dateTime) {
      if (dateTime) {
        // const date = String(dateTime).split("T");
        return `${new Date(dateTime).toLocaleString()}`;
      }
    },

    handleCaptionHide() {
      this.visibleCaption = false;
    },

    async getAllAccidents() {
      let fromDate = new Date(this.datepickervalueFrom);
      let toDate = new Date(this.datepickervalueTo);
      toDate.setHours(23, 59, 59, 999);

      const result = await this.GetAllAccidents({
        page: this.currentPage,
        incidentId: this.incidentId,
        filterVehicleNumber: this.filterVehicleNumber,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
      });

      this.perPage = result.rows;
      this.totalRows = result.totalPageNumber;
      this.allIncidents = result.data;
    },

    async getFilteredIncidents(e) {
      const data = JSON.parse(e);
      let fromDate = new Date(data.datepickervalueFrom);
      let toDate = new Date(data.datepickervalueTo);

      if (
        fromDate.toISOString().includes("1970-01-01") &&
        toDate.toISOString().includes("1970-01-01") &&
        !data.incidentId &&
        !data.filterVehicleNumber &&
        !data.vehicleClass &&
        !data.eventCategory &&
        dataspeedFilter[0] === 30 &&
        dataspeedFilter[1] === 90
      ) {
        Swal.fire("Please select filter options", "", "warning");
        this.$store.dispatch("layout/changeLoaderValue", false);
        return;
      }

      this.$store.dispatch("layout/changeLoaderValue", true);

      this.toggleFilter();
      await this.getAllAccidents();

      this.isDataFilterActive = true;
      this.$store.dispatch("layout/changeLoaderValue", false);
    },

    resetFilter() {
      this.isDataFilterActive = false;
      this.toggleFilter();
    },

    getOffence(offence) {
      return `${offence.offence_id} - ${offence.offence}`;
    },
  },

  computed: {
    masters() {
      return this.$store.getters['incident/masters']
    },
    accidentStatistics() {
      return this.$store.getters["socket/accident"].statistics;
    },
    accidents() {
      return this.$store.getters["socket/accident"].accidents;
    },
    incidentImages() {
      return this.selectedIncident.images
        ? this.selectedIncident.images.map((e) => e.media_url)
        : [];
    },
    currentUser() {
      return this.$store.getters["auth/user"];
    },
    rpoFrontCamera() {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "front_camera_url");
    },
    rpoRearCamera() {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "rear_camera_url");
    },
    rpoBodyCamera() {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "body_camera_url");
    },
    rpoGPS() {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "GPS");
    },
  },

  async mounted() {
    this.operatorId = this.currentUser.user.id;
    this.options.vehicleClass = this.masters.vehicleClass;

    this.sockets.subscribe("GET_INCIDENT_BY_ID", (data) => {
      this.allIncidents = data;
    });

    this.sockets.subscribe("RPO_LIVE_DETAILS", (data) => {
      this.rpoDeviceStats = data;
    });
  },

  watch: {
    currentPage: async function () {
      await this.getAllAccidents();
    },
    accidents: async function () {
      this.allIncidents = this.accidents.data;
      this.perPage = this.accidents.rows;
      this.totalRows = this.accidents.totalPageNumber;
    },
  },

  created() {
    this.socketInterval = setInterval(() => {
      if (!this.isDataFilterActive) {
        this.$socket.emit("REQUEST:ACCIDENT_STATISTICS");

        if (this.currentPage == 1) {
          this.$socket.emit("REQUEST:ACCIDENT_DATA");
        }
      }
    }, 10000);

    this.$socket.emit("REQUEST:ACCIDENT_STATISTICS");
    this.$socket.emit("REQUEST:ACCIDENT_DATA");
  },

  beforeDestroy() {
    if (this.socketInterval) clearInterval(this.socketInterval);
    if (this.trackingInterval) clearInterval(this.trackingInterval);
    this.socketInterval = null;
    this.trackingInterval = null;
    this.rpoDeviceStats = null;
  },
};
</script>

<style scoped>
.image {
  height: 100px;
  cursor: pointer;
  margin: 5px;
  display: inline-block;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
